import styled, { css } from 'styled-components';
import { select } from '@panel/commons/core/themev2';
import { getColor } from '@panel/commons/core/themev3';
import { site } from '@panel/commons/helpers';
import trash from '../../../../../assets/icons/trash.svg';
import trashVa from '../../../../../assets/icons/trash_VA.svg';
import trashUb from '../../../../../assets/icons/trash_UB.svg';
import trashCm from '../../../../../assets/icons/trash_CM.svg';
import trashHover from '../../../../../assets/icons/trash_hover.svg';
import trashHoverVa from '../../../../../assets/icons/trash_hover_VA.svg';
import trashHoverUb from '../../../../../assets/icons/trash_hover_UB.svg';
import trashHoverCm from '../../../../../assets/icons/trash_hover_CM.svg';

const siteId = site.id;

const getIcon = (name, type) => {
  const sites = {
    CMBR: {
      trash: { icon: trashCm, hover: trashHoverCm }
    },
    VAMX: {
      trash: { icon: trashVa, hover: trashHoverVa }
    },
    UBPE: {
      trash: { icon: trashUb, hover: trashHoverUb }
    },
    default: {
      trash: { icon: trash, hover: trashHover }
    }
  };
  return sites[siteId]?.[name][type] || sites.default[name][type];
};

const Subtitle = styled.p`
  color: ${select('neutralDarkGrayII')};
  font-family: Hind;
  font-weight: 400;
  font-size: 12px;
  line-height: 16px;
  margin-bottom: 16px;
`;

const SectionContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
`;

const ButtonsContainer = styled.div`
  display: flex;
  flex-direction: ${({ device }) => (device === 'mobile' ? 'column' : 'row')};
  gap: 8px;
  .input-container {
    display: flex;
    flex-direction: column;
    justify-content: center;
    flex-grow: 1;
    flex-basis: 0;
  }
`;

const InputVideo = styled.input`
  width: 100%;
  height: 48px;
  border-radius: 8px;
  border: 1px solid ${select('midDarkGrayAux')};
  text-indent: 10px;
  font-family: Hind;
  font-weight: 400;
  font-size: 14px;
  line-height: 24px;
  color: ${select('neutralDarkGrayII')};
  ::placeholder {
    color: ${select('midDarkGrayAux')};
  }
  :focus {
    border: 1px solid ${getColor('primary')};
    outline: none;
  }
  &.onError {
    border-color: ${select('redNoTRecommended')};
    color: ${select('midDarkGrayAux')};
  }
  :disabled {
    border: 1px solid ${select('midNeutralGray')};
    background-color: ${select('neutralGray')};
    color: ${select('midNeutralGray')};
    cursor: auto;
  }
  /* Eliminamos el fondo azul claro por default al hacer autofill/autocomplete: */
  &:-webkit-autofill,
  &:-webkit-autofill:focus {
    -webkit-box-shadow: 0 0 0px 1000px white inset;
    transition: background-color 5000s ease-in-out 0s;
    background-color: transparent;
  }
`;

const ErrorMessage = styled.span`
  color: ${props => (props.notRed ? select('black') : select('redNoTRecommended'))};
  display: inherit;
  padding: 7px 0px 0px;
  font-size: 12px;
`;

const ErrorMessageLg = styled.span`
  color: ${props => (props.notRed ? select('black') : select('redNoTRecommended'))};
  display: inherit;
  padding: 7px 0px 0px;
  font-size: 12px;
`;

const ErrorMessageMobile = styled.span`
  color: ${props => (props.notRed ? select('black') : select('redNoTRecommended'))};
  display: none;
  padding: 7px 0px 0px;
`;

const AddButton = styled.button`
  width: ${({ device }) => (device === 'mobile' ? '100%' : '90px')};
  height: 48px;
  border-radius: 8px;
  border: 1px solid ${getColor('primary')};
  flex: 0 0 auto;
  cursor: pointer;
  font-family: Hind;
  font-weight: 600;
  font-size: 16px;
  line-height: 130%;
  text-align: center;
  :disabled {
    border: 1px solid ${select('midNeutralGray')};
    color: ${select('midNeutralGray')};
    cursor: auto;
  }
`;

const VideoList = styled.div`
  max-height: ${({ maxHeight }) => `calc(${maxHeight} + 0.5px)`};
  display: grid;
  gap: 16px;
  grid-template-columns: ${({ device, columnsNumber = 2 }) =>
    device === 'mobile' ? '1fr' : `repeat(${columnsNumber}, 1fr)`};
  transition: max-height 0.3s ease;
  overflow: hidden;
  margin-top: 16px;
  margin-bottom: ${({ hasToggleButton }) => (hasToggleButton ? '24px' : '0')};
`;

const Video = styled.div`
  iframe {
    width: 100%;
    height: ${({ device }) => {
      if (device === 'mobile') return '178px';
      if (device === 'tablet') return '136px';
      return '154px';
    }};
    border-radius: 8px 8px 0px 0px;
    @media (min-width: 399px) {
      ${({ device }) =>
        device === 'mobile' &&
        css`
          height: 207px;
        `}
    }
  }
`;

const VideoDetail = styled.div`
  height: 46.65px;
  width: 100%;
  background: ${select('white')};
  border: 0.5px solid ${select('neutralDarkGray')};
  border-radius: 0px 0px 8px 8px;
  margin-top: -8px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px;
  gap: 1px;
  span:first-child {
    font-family: Hind;
    font-weight: 400;
    font-size: 8px;
    line-height: 16px;
    white-space: break-spaces;
    width: ${({ device }) => {
      if (device === 'mobile') return '265px';
      if (device === 'tablet') return '180px';
      return '215px';
    }};
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

const TrashIcon = styled.span`
  width: 32px;
  height: 32px;
  background-image: url(${getIcon('trash', 'icon')});
  background-position: center;
  background-size: contain;
  background-repeat: no-repeat;
  cursor: pointer;
  &:hover {
    background-image: url(${getIcon('trash', 'hover')});
  }
`;

const ToggleVideo = styled.button`
  margin-top: 24px;
  cursor: pointer;
  display: flex;
  span {
    height: 18px;
    font-family: Hind;
    font-size: 14px;
    font-weight: 600;
    line-height: 1.3;
    color: ${select('black')};
  }
  svg {
    margin-left: 8px;
  }
`;

const ContainerCheck = styled.div`
  display: flex;
  margin-top: 24px;
  input {
    margin-right: 8px;
  }
`;

const LabelCheckPortada = styled.label``;

export {
  Subtitle,
  SectionContainer,
  ButtonsContainer,
  InputVideo,
  ErrorMessage,
  AddButton,
  VideoList,
  Video,
  VideoDetail,
  TrashIcon,
  ToggleVideo,
  ContainerCheck,
  LabelCheckPortada,
  ErrorMessageLg,
  ErrorMessageMobile
};
